import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router";
import { AppShell } from "./components/layout/AppShell";
import { useAuth } from "./hooks/useAuth";
import { Analytics } from "./pages/Analytics";
import { BrandConfig } from "./pages/BrandConfig";
import { Dashboard } from "./pages/Dashboard";
import { Generate } from "./pages/Generate";
import { Login } from "./pages/Login";
import { PostDetail } from "./pages/PostDetail";
import { Review } from "./pages/Review";
import { Schedule } from "./pages/Schedule";
import { Settings } from "./pages/Settings";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Top-level route guard. Three states:
 *   - loading      → blank/loader so we don't briefly flash the login page
 *                    on a refresh where the user is actually logged in
 *   - no session   → render <Navigate to="/login"/>
 *   - session ok   → render children
 *
 * The same check exists server-side; this is purely UX so the user sees
 * the right thing rather than 401-flashing JSON.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-beige">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-brand-text-dark/40">
          Lade…
        </div>
      </div>
    );
  }
  if (!data) {
    const next = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="generate" element={<Generate />} />
            <Route path="posts/:id" element={<PostDetail />} />
            <Route path="review" element={<Review />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="config" element={<BrandConfig />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
